import React, { useEffect, useState } from "react";
import api, { brl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Lightbulb, ShoppingBag } from "lucide-react";

export const PurchaseSuggestions = ({ reloadKey, onCreate }) => {
  const [data, setData] = useState({ days: 30, items: [] });
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState({});

  useEffect(() => {
    api.get("/purchases/suggestions").then((r) => {
      setData(r.data);
      setSelected(Object.fromEntries(r.data.items.map((i) => [i.product_id, true])));
      setQty(Object.fromEntries(r.data.items.map((i) => [i.product_id, i.suggested_qty])));
    }).catch(() => setData({ days: 30, items: [] }));
  }, [reloadKey]);

  const qtyOf = (i) => Number(qty[i.product_id]) || 0;
  const chosen = data.items.filter((i) => selected[i.product_id] && qtyOf(i) > 0).map((i) => ({ ...i, suggested_qty: qtyOf(i) }));
  const estimated = chosen.reduce((s, i) => s + i.suggested_qty * (i.cost_price || 0), 0);

  return (
    <Card className="card-outline" data-testid="purchase-suggestions">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-lg font-bold flex items-center gap-2"><Lightbulb className="w-5 h-5 text-amber-500" /> Sugestão de compras</h2>
            <p className="text-xs text-slate-500">Produtos no estoque mínimo ou abaixo. Quantidade = vendas dos últimos {data.days} dias + mínimo − estoque atual − pedidos a receber.</p>
          </div>
          <Button onClick={() => onCreate(chosen)} disabled={!chosen.length} className="bg-blue-600 hover:bg-blue-700" data-testid="create-from-suggestions-btn">
            <ShoppingBag className="w-4 h-4 mr-2" /> Gerar pedido ({chosen.length}) • {brl(estimated)}
          </Button>
        </div>
        <div className="overflow-x-auto border rounded-lg">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-10"></TableHead><TableHead>Produto</TableHead>
              <TableHead className="text-right">Estoque</TableHead><TableHead className="text-right">Mínimo</TableHead>
              <TableHead className="text-right">Vendido ({data.days}d)</TableHead><TableHead className="text-right">A receber</TableHead>
              <TableHead className="text-right">Sugerido</TableHead><TableHead className="text-right">Comprar</TableHead><TableHead className="text-right">Custo est.</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.items.map((i) => (
                <TableRow key={i.product_id} data-testid={`suggestion-row-${i.product_id}`}>
                  <TableCell>
                    <Checkbox checked={!!selected[i.product_id]} data-testid={`suggestion-check-${i.product_id}`}
                      onCheckedChange={(v) => setSelected((s) => ({ ...s, [i.product_id]: !!v }))} />
                  </TableCell>
                  <TableCell className="font-medium">{i.name}</TableCell>
                  <TableCell className="text-right kpi-num"><Badge variant="outline" className="border-rose-400 text-rose-600">{i.stock}</Badge></TableCell>
                  <TableCell className="text-right kpi-num">{i.min_stock}</TableCell>
                  <TableCell className="text-right kpi-num">{i.sold_period}</TableCell>
                  <TableCell className="text-right kpi-num">{i.pending_qty}</TableCell>
                  <TableCell className="text-right kpi-num font-semibold text-blue-600">{i.suggested_qty}</TableCell>
                  <TableCell className="text-right">
                    <Input type="number" min="0" step="any" value={qty[i.product_id] ?? ""} className="h-8 w-20 text-right ml-auto"
                      onChange={(e) => setQty((q) => ({ ...q, [i.product_id]: e.target.value }))} data-testid={`suggestion-qty-${i.product_id}`} />
                  </TableCell>
                  <TableCell className="text-right kpi-num">{brl(qtyOf(i) * (i.cost_price || 0))}</TableCell>
                </TableRow>
              ))}
              {data.items.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-6">Nenhum produto precisa de reposição agora.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
